/**
 * The cabinet's build-time configuration.
 *
 * THE API BASE URL IS ONE OF THE TWO BUILD-TIME FACTS THE DEPLOY SMOKE CHECK EXISTS TO PROVE.
 * "A wrong one is a white page" (adr/0007-web-app-stack.md), so it lives in a module a test can
 * import rather than being interpolated into a fetch call somewhere in a screen.
 *
 * The cabinet is served from `authors.moonx.app` and calls `moonx.art/authors-api/*`
 * CROSS-ORIGIN with a bearer token — one CORS allowlist entry on the API side. That is exactly
 * why the cabinet may live on another hostname at all (adr/0002-edge-addressing.md,
 * adr/0007-web-app-stack.md).
 *
 * Per-environment values are Vite BUILD-TIME env, prod and local dev only (H7). The default below
 * is production, so a build with no env file still points somewhere real; a dev build overrides it
 * with `VITE_AUTHORS_API_BASE_URL` in `.env.development`.
 */

const DEFAULT_AUTHORS_API_BASE_URL = 'https://moonx.art/authors-api';

/**
 * ★ THE TUNNEL MODE (`dev:tunnel`): a base that starts with `/` is THIS PAGE'S OWN ORIGIN plus that
 * path. The dev server forwards it to the API on the laptop (`vite.config.ts`), so one tunnel to the
 * dev server carries the cabinet AND its API, and a teammate's browser never calls `127.0.0.1` - which
 * on their machine is their own. Every other value is used as written.
 */
export function apiBaseUrlFrom(configured: string | undefined, pageOrigin: string | null): string {
  const value =
    typeof configured === 'string' && configured.length > 0
      ? configured
      : DEFAULT_AUTHORS_API_BASE_URL;
  return value.startsWith('/') && pageOrigin !== null ? `${pageOrigin}${value}` : value;
}

function readApiBaseUrl(): string {
  const value = apiBaseUrlFrom(
    import.meta.env.VITE_AUTHORS_API_BASE_URL,
    typeof window === 'undefined' ? null : window.location.origin,
  );

  // Fail loudly at module load rather than at the first request. A malformed base produces a
  // stream of confusing network errors much later, in whichever screen fetches first.
  try {
    new URL(value);
  } catch {
    throw new Error(
      `VITE_AUTHORS_API_BASE_URL is not a valid absolute URL: ${value}. The cabinet calls the API ` +
        'cross-origin, so a relative base cannot work here.',
    );
  }

  // A trailing slash turns every `${base}/articles` into a double slash, which some proxies
  // normalise and some do not.
  return value.replace(/\/+$/, '');
}

export const AUTHORS_API_BASE_URL = readApiBaseUrl();

/** The cabinet owns its whole hostname, so its base path is the root. */
export const APP_BASE_URL = import.meta.env.BASE_URL;

/**
 * ★ THE ONE LIVE FLAG. `VITE_AUTHORS_API_LIVE=true` points EVERY transport in this app at the API
 * named above: the cabinet, the editor, the calendar, the application flow, the support pages and
 * the identity operations. Anything else - unset, `false`, a typo - means fixtures everywhere.
 *
 * IT LIVES HERE AND NOWHERE ELSE, and that is the fix rather than a tidy-up. Before it, three
 * transports read this variable through `features/cabinet/api/cabinet-api-source.ts`, the
 * application flow read a hard-coded `APPLICATION_API_TRANSPORT = 'fixture'`, the support pages a
 * hard-coded fixture, and the identity operations a SECOND variable (`VITE_AUTHORS_API_TRANSPORT`).
 * A build could therefore point the article list at the network and the application form at
 * fixtures - two halves of one person's session disagreeing about which platform they are on,
 * which is the failure `editor-api-source.ts` already names for two of them. This module is
 * the one every transport already imports for the base URL, so it is the one that can hold the
 * flag without any feature importing another.
 *
 * A FUNCTION, NOT A CONSTANT, so a spec can flip it with `vi.stubEnv` and read the answer at call
 * time. In a build Vite replaces the expression with a literal, so the cost is nothing.
 *
 * The default is fixtures because the accessibility leg (s11.7) checks the DEFAULT build with every
 * seam on its fixture - "THE SPA SEAM DEFAULTS TO FIXTURES. THE LEG NEEDS NO LIVE API"
 * (`scripts/a11y-roots.mjs`) - and a default of `live` would turn every screen it checks into
 * an error state.
 */
export function isLiveApiConfigured(): boolean {
  return import.meta.env.VITE_AUTHORS_API_LIVE === 'true';
}
